import { Controller } from "@hotwired/stimulus"

// Field-builder for a subLineText definition. Rows are server-rendered from the
// design-system Ui::* components; new rows are cloned from a <template> (Stimulus
// connects the ui--select / Tom Select controllers on the clones automatically).
//
// The structured widgets are the primary editing surface. On every change they
// regenerate the JSON in the (editable) advanced textarea, which is the ONLY field
// posted. Sync is one-directional (form -> JSON): editing the JSON by hand is
// submitted as-is, but touching any widget regenerates and overwrites it.
export default class extends Controller {
  static targets = ["fields", "rowTemplate", "separator", "json", "empty"]

  connect() {
    this.syncAllRows()
    this.regenerate()
  }

  // ── Row actions ──────────────────────────────────────────────────────────
  addField() {
    const fragment = this.rowTemplateTarget.content.cloneNode(true)
    this.fieldsTarget.appendChild(fragment)
    this.syncAllRows()
    this.toggleEmpty()
    this.regenerate()
  }

  removeField(event) {
    event.target.closest("[data-slt-row]")?.remove()
    this.toggleEmpty()
    this.regenerate()
  }

  moveUp(event) {
    const row = event.target.closest("[data-slt-row]")
    const prev = row?.previousElementSibling
    if (prev && prev.hasAttribute("data-slt-row")) prev.before(row)
    this.regenerate()
  }

  moveDown(event) {
    const row = event.target.closest("[data-slt-row]")
    const next = row?.nextElementSibling
    if (next && next.hasAttribute("data-slt-row")) next.after(row)
    this.regenerate()
  }

  // Delegated change/input handler (bound on the controller root so it also
  // catches Tom Select's change events, which don't reliably trigger
  // element-level Stimulus actions).
  onChange(event) {
    const row = event.target.closest?.("[data-slt-row]")
    if (row) this.syncRow(row)
    this.regenerate()
  }

  // ── Conditional visibility ─────────────────────────────────────────────────
  syncAllRows() {
    this.fieldsTarget
      .querySelectorAll("[data-slt-row]")
      .forEach((row) => this.syncRow(row))
  }

  syncRow(row) {
    const kind = this.val(row, "kind")
    row
      .querySelector('[data-slt-block="component"]')
      ?.classList.toggle("hidden", kind !== "component")
    row
      .querySelector('[data-slt-block="computed"]')
      ?.classList.toggle("hidden", kind !== "computed")

    const by = this.val(row, "roleBy") || "componentType"
    row
      .querySelector('[data-slt-rolevalue="componentType"]')
      ?.classList.toggle("hidden", by !== "componentType")
    row
      .querySelector('[data-slt-rolevalue="category"]')
      ?.classList.toggle("hidden", by !== "category")

    row
      .querySelector('[data-slt-block="mapDict"]')
      ?.classList.toggle("hidden", this.val(row, "map") !== "custom")
  }

  // ── Build JSON from the widgets ────────────────────────────────────────────
  regenerate() {
    const fields = []
    this.fieldsTarget.querySelectorAll("[data-slt-row]").forEach((row) => {
      const kind = this.val(row, "kind")
      let source
      if (kind === "computed") {
        const resolver = this.val(row, "resolver")
        if (!resolver) return
        source = { kind: "computed", resolver }
      } else {
        const by = this.val(row, "roleBy") || "componentType"
        const value =
          by === "category"
            ? this.val(row, "roleValueCategory")
            : this.val(row, "roleValueComponentType")
        const attribute = this.val(row, "attribute")
        if (!value && !attribute) return
        source = { kind: "component", role: { by, value }, attribute }
      }

      const field = { source }
      const label = this.val(row, "label")
      if (label) field.label = label
      const suffix = this.val(row, "suffix")
      if (suffix) field.suffix = suffix
      // "custom" is an editor-only choice: what gets stored is the dictionary
      // written in the textarea. Invalid JSON leaves the field without a map
      // rather than corrupting the definition.
      const map = this.val(row, "map")
      if (map === "custom") {
        try {
          const parsed = JSON.parse(this.val(row, "mapDict"))
          if (parsed && typeof parsed === "object") field.map = parsed
        } catch {
          // Keep the field mapless while the JSON is being typed.
        }
      } else if (map) {
        field.map = map
      }
      const op = this.val(row, "transformOp")
      const tvalue = this.val(row, "transformValue")
      if (op && tvalue !== "") field.transform = { op, value: Number(tvalue) }

      fields.push(field)
    })

    // An empty separator is the default, so it is left out of the JSON: each
    // field carries its own punctuation in its prefix.
    const definition = { fields }
    const separator = this.hasSeparatorTarget ? this.separatorTarget.value : ""
    if (separator) definition.separator = separator
    if (this.hasJsonTarget)
      this.jsonTarget.value = JSON.stringify(definition, null, 2)
  }

  // ── Helpers ────────────────────────────────────────────────────────────────
  val(row, name) {
    return row.querySelector(`[data-slt="${name}"]`)?.value ?? ""
  }

  toggleEmpty() {
    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle(
        "hidden",
        this.fieldsTarget.querySelectorAll("[data-slt-row]").length > 0,
      )
    }
  }
}
